import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import CandidateDetailsModal from "../../../../utils/CandidateDetailsModal";
import ApplyStageModal from "../../../../utils/applyStageModal";
import UploadCandidateModal from "../../../../utils/UploadCandidateModal";
import ResumeUploadModal from "../../../../utils/ResumeUploadModal";
import useCandidatesV2 from "../../../../utils/useCandidatesV2";
import { openCandidateProfile } from "../constants";
import AdvancedSearchSidebar from "./AdvancedSearchSidebar";
import CandidatesHeader from "./CandidatesHeader";
import BulkActionBar from "./BulkActionBar";
import CandidatesTable from "./CandidatesTable";
import CandidatesPagination from "./CandidatesPagination";
import EditCandidateModal from "../modals/EditCandidateModal";
import StatusChangeModal from "../modals/StatusChangeModal";
import AssignCandidateModal from "../modals/AssignCandidateModal";
import ClearAssignmentModal from "../modals/ClearAssignmentModal";
import SaveSearchModal from "../modals/SaveSearchModal";
import SavedSearchesModal from "../modals/SavedSearchesModal";

const RESUME_MIME_TYPES = ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
const EMPTY_STATUS_CHANGE = { open: false, candidateId: null, status: null, fromName: "", comment: "" };

// The original one-row-per-candidate view. Kept mounted (just hidden) while the Activity-wise
// tab is open so filters, page and selection survive a tab switch.
const CandidateWiseView = ({ access, hidden, toolbarSlot }) => {
  const cv = useCandidatesV2();
  const {
    jobId,
    selectedJobs, setSelectedJobs,
    file, setFile,
    snackOpen, snackSeverity, snackMessage,
    uploading, loading,
    uploadProgress, assignProgress,
    editModal, setEditModal, editForm, setEditForm,
    resumeModal, setResumeModal,
    showUploadModal,
    isOpen, setIsOpen,
    pageSize, setPageSize, currentPage, setCurrentPage,
    saveSearchModal, setSaveSearchModal, savedSearchesModal, setSavedSearchesModal,
    csvInputRef, pdfInputRef, resumeInputRef,
    candidateList, pagination, totalPages, hiringStatuses,
    currentJobs, isDisabled, activeFilterChips,
    refreshCandidate,
    handleConfirmSaveSearch, handleLoadSavedSearch, handleDeleteSavedSearch,
    handleCsvUpload, handlePdfUpload, resetUploadModal, handleCandidateUpload, downloadSampleCsv,
    handleResumeUpload, handleEditCandidate,
    handleSelectJob,
    showSnack, snackClosed,
    handleStatusChange, handleBulkStatusChange, statusLoading,
    openApplyStageModal, selectAssignedStage,
    getCandidateStageScore, getDaysFromToday,
  } = cv;
  const { teamAccessEnabled, isRestricted, canAccessCandidate, getAssignee, assignableMembers, setAssignee } = access;

  const navigate = useNavigate();
  const [assignCandidate, setAssignCandidate] = useState(null);
  const [clearCandidate, setClearCandidate] = useState(null);
  const [detailsModal, setDetailsModal] = useState({ type: null, candidate: null });
  const [statusChangeModal, setStatusChangeModal] = useState(EMPTY_STATUS_CHANGE);

  // ── Selection (only candidates the user can access are selectable) ─────────
  const accessibleIds = candidateList.filter(canAccessCandidate).map((c) => c.id);
  const allAccessibleSelected = accessibleIds.length > 0 && accessibleIds.every((id) => selectedJobs.includes(id));
  const handleSelectAllAccessible = (e) => setSelectedJobs(e.target.checked ? accessibleIds : []);

  // Drop selections that are no longer accessible (e.g. access info loaded after a selection).
  useEffect(() => {
    const allowed = new Set(accessibleIds);
    if (selectedJobs.some((id) => !allowed.has(id))) setSelectedJobs((prev) => prev.filter((id) => allowed.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessibleIds.join(",")]);

  // ── Row actions ────────────────────────────────────────────────────────────
  const openEditModal = (candidate) => {
    setEditModal({ open: true, candidate });
    setEditForm({
      name: candidate?.name || "",
      email: candidate?.email || "",
      phone_number: candidate?.phone_number || "",
    });
  };

  const handleRowAction = (key, candidate) => {
    if (key === "view") openCandidateProfile(jobId, candidate.id);
    else if (key === "edit") openEditModal(candidate);
    else setDetailsModal({ type: key, candidate });
  };

  const closeDetails = () => setDetailsModal({ type: null, candidate: null });

  // After a message is sent from the popup, continue the conversation on the candidate profile.
  const handleMessageSent = () => {
    const candidateId = detailsModal.candidate?.id;
    closeDetails();
    if (candidateId) navigate(`/jobs/${jobId}/candidates/${candidateId}?tab=all`, { state: { openChatTab: "messages" } });
  };

  // ── Assignment ─────────────────────────────────────────────────────────────
  // Resolves to true on success so the calling modal knows whether to stay open.
  const saveAssignee = async (candidateId, companyUserId) => {
    const res = await setAssignee(candidateId, companyUserId);
    if (!res.success) {
      showSnack(res.message || (companyUserId ? "Failed to update assignee" : "Failed to remove assignment"));
      return false;
    }
    // Refresh just this row before closing, so the modal stays in its saving state until the row shows the new assignee.
    await refreshCandidate(candidateId);
    showSnack(companyUserId ? "Candidate assigned successfully" : "Assignment removed", "success");
    setAssignCandidate(null);
    setClearCandidate(null);
    return true;
  };

  // ── Hiring status change ───────────────────────────────────────────────────
  const closeStatusChangeModal = () => setStatusChangeModal(EMPTY_STATUS_CHANGE);

  const handleConfirmStatusChange = async () => {
    const { candidateId, status, comment } = statusChangeModal;
    if (!comment.trim()) { showSnack("Please add a comment before updating the status."); return; }
    await handleStatusChange(candidateId, status, comment);
    closeStatusChangeModal();
  };

  const closeSaveSearch = () => setSaveSearchModal({ open: false, name: "" });
  const closeSavedSearches = () => setSavedSearchesModal((prev) => ({ ...prev, open: false }));

  return (
    <div className="candidates-v2-page" style={hidden ? { display: "none" } : undefined}>
      <AdvancedSearchSidebar cv={cv} />

      <main className="candidates-v2-main">
        <CandidatesHeader cv={cv} isRestricted={isRestricted} actionsSlot={hidden ? null : toolbarSlot} />

        {selectedJobs.length > 0 && (
          <BulkActionBar
            count={selectedJobs.length}
            disabled={isDisabled}
            onAssignStage={openApplyStageModal}
            onBulkStatus={handleBulkStatusChange}
          />
        )}

        <div className="candidates-table-outer">
          <CandidatesTable
            jobId={jobId}
            loading={loading}
            candidateList={candidateList}
            currentJobs={currentJobs}
            selectedJobs={selectedJobs}
            onToggleSelect={handleSelectJob}
            allSelected={allAccessibleSelected}
            onSelectAll={handleSelectAllAccessible}
            selectableCount={accessibleIds.length}
            isRestricted={isRestricted}
            access={access}
            showAssigneeColumn={teamAccessEnabled}
            onAssign={setAssignCandidate}
            onQuickClear={setClearCandidate}
            hiringStatuses={hiringStatuses}
            statusLoading={statusLoading}
            onStatusPick={(candidateId, status, fromName) =>
              setStatusChangeModal({ open: true, candidateId, status, fromName, comment: "" })}
            onAction={handleRowAction}
            getCandidateStageScore={getCandidateStageScore}
            getDaysFromToday={getDaysFromToday}
            disabled={isDisabled}
          />
          {!loading && (
            <CandidatesPagination
              pagination={pagination}
              totalPages={totalPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              disabled={isDisabled}
            />
          )}
        </div>
      </main>

      {/* Hidden file inputs */}
      <input ref={csvInputRef} type="file" accept=".csv,.xls,.xlsx" style={{ display: "none" }} onChange={handleCsvUpload} />
      <input ref={pdfInputRef} type="file" accept=".pdf,.docx" multiple style={{ display: "none" }} onChange={handlePdfUpload} />
      <input ref={resumeInputRef} type="file" accept=".pdf,.docx" style={{ display: "none" }} />

      {showUploadModal && (
        <UploadCandidateModal show={showUploadModal} uploading={uploading} file={file} uploadProgress={uploadProgress}
          csvInputRef={csvInputRef} pdfInputRef={pdfInputRef}
          handleCsvClick={() => csvInputRef.current.click()} handlePdfClick={() => pdfInputRef.current.click()}
          handleCsvUpload={handleCsvUpload} handlePdfUpload={handlePdfUpload}
          handleCandidateUpload={handleCandidateUpload} resetUploadModal={resetUploadModal}
          downloadSampleCsv={downloadSampleCsv} />
      )}

      <ResumeUploadModal show={resumeModal.open} uploading={uploading} file={file} resumeInputRef={resumeInputRef}
        onClose={() => setResumeModal({ open: false, candidateId: null })}
        onFileSelect={(e) => {
          const f = e.target.files[0];
          if (!f || !RESUME_MIME_TYPES.includes(f.type)) {
            showSnack("Only PDF and DOCX files are supported."); return;
          }
          setFile(f);
        }}
        onUploadClick={handleResumeUpload} />

      {editModal.open && (
        <EditCandidateModal
          form={editForm}
          setForm={setEditForm}
          onClose={() => setEditModal({ open: false, candidate: null })}
          onSubmit={handleEditCandidate}
          disabled={isDisabled}
        />
      )}

      {/* Messages / Activity / Notes / Files popup */}
      {detailsModal.type && (
        <CandidateDetailsModal
          type={detailsModal.type}
          jobId={jobId}
          candidate={detailsModal.candidate}
          onClose={closeDetails}
          onMessageSent={handleMessageSent}
          onSwitch={(type) => setDetailsModal((prev) => ({ ...prev, type }))}
        />
      )}

      {statusChangeModal.open && (
        <StatusChangeModal
          fromName={statusChangeModal.fromName}
          toName={statusChangeModal.status?.name}
          comment={statusChangeModal.comment}
          onCommentChange={(comment) => setStatusChangeModal((m) => ({ ...m, comment }))}
          onClose={closeStatusChangeModal}
          onConfirm={handleConfirmStatusChange}
          saving={!!statusLoading[statusChangeModal.candidateId]}
        />
      )}

      {assignCandidate && (
        <AssignCandidateModal
          candidate={assignCandidate}
          members={assignableMembers}
          onSave={(companyUserId) => saveAssignee(assignCandidate.id, companyUserId)}
          onClose={() => setAssignCandidate(null)}
        />
      )}

      {clearCandidate && (
        <ClearAssignmentModal
          candidateName={clearCandidate.name}
          assigneeName={getAssignee(clearCandidate)?.name}
          onConfirm={() => saveAssignee(clearCandidate.id, null)}
          onClose={() => setClearCandidate(null)}
        />
      )}

      {isOpen && (
        <ApplyStageModal isOpen={isOpen} setIsOpen={() => setIsOpen(false)}
          selectAssignedStage={selectAssignedStage} jobId={currentJobs[0]?.job_id}
          loading={loading} assignProgress={assignProgress} />
      )}

      {saveSearchModal.open && (
        <SaveSearchModal
          name={saveSearchModal.name}
          onNameChange={(name) => setSaveSearchModal((prev) => ({ ...prev, name }))}
          filterCount={activeFilterChips.length}
          onClose={closeSaveSearch}
          onConfirm={handleConfirmSaveSearch}
        />
      )}

      {savedSearchesModal.open && (
        <SavedSearchesModal
          list={savedSearchesModal.list}
          loading={savedSearchesModal.listLoading}
          onLoad={handleLoadSavedSearch}
          onDelete={handleDeleteSavedSearch}
          onClose={closeSavedSearches}
        />
      )}

      <Snackbar anchorOrigin={{ vertical: "top", horizontal: "center" }} open={snackOpen} autoHideDuration={3000} onClose={snackClosed}>
        <Alert onClose={snackClosed} severity={snackSeverity} sx={{ width: "100%" }}>{snackMessage}</Alert>
      </Snackbar>
    </div>
  );
};

export default CandidateWiseView;
